import {skipToken} from '@reduxjs/toolkit/query';
import {useJobContext} from 'contexts/JobContext';
import {useNotificationContext} from 'contexts/NotificationContext';
import {getProductParams} from 'hooks/preCacheProducts';
import {useEffect, useState} from 'react';
import {getAllItems} from 'service/HardwareService';
import {getQuickProducts} from 'service/ProductService';
import {genericMessageHandler, usePromise} from 'shared/helpers';
import {getErrorMessage} from 'shared/helpers/getErrorMessage';

export const RECENTLY_ADDED_KEYS = {
    PRODUCT: 'product_recently_added',
    HARDWARE: 'hardware_recently_added',
};

export const addRecentItem = (itemId, type) => {
    const recentItemsString = localStorage.getItem(type);
    let existingItems = [];

    if (recentItemsString != '' && recentItemsString != null) {
        existingItems = JSON.parse(recentItemsString);
    }

    if (!existingItems.includes(itemId)) {
        existingItems.unshift(itemId);

        if (existingItems.length > 10) existingItems.pop();
    } else {
        existingItems.splice(existingItems.indexOf(itemId), 1);
        existingItems.unshift(itemId);
    }

    localStorage.setItem(type, JSON.stringify(existingItems));
};

export const useRecentlyAddedActions = () => {
    // NOTE: remove this later so that function is always directly accessed
    // and no through a hook
    return {
        addRecentItem,
    };
};

export const useRecentlyAdded = (isProduct, setError) => {
    const [items, setItems] = useState([]);
    const {notify} = useNotificationContext();
    const {room} = useJobContext();

    useEffect(() => {
        const items = localStorage.getItem(
            isProduct
                ? RECENTLY_ADDED_KEYS.PRODUCT
                : RECENTLY_ADDED_KEYS.HARDWARE
        );
        let recentlyAdded = [];

        if (items != '' && items != null) {
            recentlyAdded = JSON.parse(items);
        }

        if (recentlyAdded.length) {
            const params = getProductParams({room});
            if (params === skipToken) return;

            const promise = isProduct
                ? getQuickProducts(params)
                : getAllItems();
            const sortFn = isProduct
                ? (productA, productB) =>
                      productA.text.localeCompare(productB.text)
                : (productA, productB) =>
                      productA.name.localeCompare(productB.name);
            return usePromise(
                ([products]) => {
                    if (products && products.length) {
                        products = products
                            .filter((product) =>
                                recentlyAdded.includes(product.id)
                            )
                            .sort(sortFn);

                        if (isProduct) {
                            setItems(
                                products.map((product) => {
                                    const productInfo = {
                                        ...product,
                                        ...{
                                            imageUrl: product.image,
                                        },
                                    };

                                    if (!productInfo.hasOwnProperty('name')) {
                                        productInfo.name = product.text;
                                    }

                                    return productInfo;
                                })
                            );
                        } else {
                            setItems(products);
                        }
                    }
                },
                [promise],
                (error, isCancelled) => {
                    if (isCancelled.value) return;

                    const emptySundries =
                        error?.status === 400 &&
                        error?.data?.success === 0 &&
                        Array.isArray(error?.data?.sundry_items) &&
                        error?.data?.sundry_items.length === 0;

                    genericMessageHandler(notify, {
                        message: emptySundries
                            ? 'No sundry items are currently available'
                            : getErrorMessage(error),
                    });
                }
            );
        }
    }, [room]);

    return {
        items,
    };
};
